import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Handshake, Users, Plus, Trash2, Star, Mail, Phone, Building2, Loader2,
  KanbanSquare, GripVertical,
} from "lucide-react";

const KINDS = [
  { v: "title", l: "Title Company" },
  { v: "tc", l: "Transaction Coordinator" },
  { v: "attorney", l: "Attorney" },
  { v: "inspector", l: "Inspector" },
  { v: "lender", l: "Lender" },
];

const STAGE_LABELS = {
  opened: "Opened", title_ordered: "Title Ordered", inspection: "Inspection",
  appraisal: "Appraisal", clear_to_close: "Clear to Close", funded: "Funded",
  closed_won: "Closed 🎉", dead: "Dead",
};

const STAGE_COLOR = {
  opened: "#94A3B8", title_ordered: "#0EA5E9", inspection: "#8B5CF6",
  appraisal: "#F59E0B", clear_to_close: "#10B981", funded: "#059669",
  closed_won: "#0B1D3B", dead: "#991B1B",
};

const STAGE_ORDER = [
  "opened", "title_ordered", "inspection", "appraisal",
  "clear_to_close", "funded", "closed_won", "dead",
];

export default function DispoClosings() {
  const [tab, setTab] = useState("closings");

  return (
    <div className="space-y-6" data-testid="dispo-closings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Handshake size={20} className="text-[var(--gold,#F59E0B)]" />
            <h1 className="font-display font-extrabold text-3xl tracking-tight">Closing Studio</h1>
            <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">Dispo Studio · Week 5</span>
          </div>
          <p className="text-sm text-[var(--ink-2)] max-w-2xl">
            Every accepted offer becomes a live closing you can track — Title Company, TC, milestones, and target date in one place.
          </p>
        </div>
        <Link to="/app/dispo" className="btn-ghost text-sm">← Back to Deal Rooms</Link>
      </div>

      <div className="flex gap-2 border-b border-[var(--line)]">
        <TabBtn active={tab === "closings"} onClick={() => setTab("closings")} testid="tab-closings">
          <Handshake size={14} /> Active Closings
        </TabBtn>
        <TabBtn active={tab === "kanban"} onClick={() => setTab("kanban")} testid="tab-kanban">
          <KanbanSquare size={14} /> Pipeline Board
        </TabBtn>
        <TabBtn active={tab === "team"} onClick={() => setTab("team")} testid="tab-team">
          <Users size={14} /> Closing Team
        </TabBtn>
      </div>

      {tab === "closings" && <ClosingsPanel />}
      {tab === "kanban" && <KanbanPanel />}
      {tab === "team" && <TeamPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 font-semibold text-sm flex items-center gap-2 border-b-2 -mb-px transition ${active ? "border-[var(--gold,#F59E0B)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]"}`}
    >{children}</button>
  );
}

function ClosingsPanel() {
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dispo/closings");
      setClosings(data.closings || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load closings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStage = async (id, stage) => {
    try {
      const { data } = await api.patch(`/dispo/closings/${id}`, { stage });
      setClosings((cs) => cs.map((c) => (c.id === id ? { ...c, ...data.closing } : c)));
      toast.success(`Stage → ${STAGE_LABELS[stage] || stage}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this closing? The offer will remain accepted on the room.")) return;
    try {
      await api.delete(`/dispo/closings/${id}`);
      setClosings((cs) => cs.filter((c) => c.id !== id));
      toast.success("Closing removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading) return <div className="p-10 text-center text-[var(--ink-3)]">Loading…</div>;
  if (closings.length === 0) return (
    <div className="rounded-xl border border-dashed border-[var(--line)] bg-white p-10 text-center" data-testid="closings-empty">
      <Handshake size={40} className="mx-auto text-[var(--ink-3)] mb-3" />
      <h3 className="font-display font-bold text-xl mb-1">No closings yet</h3>
      <p className="text-sm text-[var(--ink-2)] max-w-md mx-auto">
        Accept an offer on any Deal Room, then click <b>Send to closing</b> — it lands here with the buyer, price, and your default Title Co pre-filled.
      </p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="closings-list">
      {closings.map((c) => (
        <div key={c.id} className="rounded-xl border border-[var(--line)] bg-white p-5" data-testid={`closing-${c.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display font-bold text-lg truncate">{c.room_title || "Untitled deal"}</div>
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
                  style={{ background: (STAGE_COLOR[c.stage] || "#94A3B8") + "22", color: STAGE_COLOR[c.stage] || "#475569" }}
                >{STAGE_LABELS[c.stage] || c.stage}</span>
              </div>
              <div className="text-xs text-[var(--ink-2)]">{c.property_address}</div>
              <div className="text-xs text-[var(--ink-2)] mt-2 flex flex-wrap gap-3">
                <span><b>{c.buyer_name}</b> — {fmtMoney(c.offer_price)}</span>
                {c.title_company && <span>🏛️ {c.title_company.name}{c.title_company.company ? ` · ${c.title_company.company}` : ""}</span>}
                {c.tc && <span>👤 {c.tc.name}</span>}
                {c.target_close_date && <span>🎯 Close by {c.target_close_date}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select
                value={c.stage}
                onChange={(e) => setStage(c.id, e.target.value)}
                className="input text-xs py-1.5"
                data-testid={`closing-stage-${c.id}`}
              >
                {Object.keys(STAGE_LABELS).map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
              <button
                type="button"
                onClick={() => remove(c.id)}
                className="text-[var(--ink-3)] hover:text-red-600"
                title="Delete closing"
                data-testid={`closing-delete-${c.id}`}
              ><Trash2 size={14} /></button>
            </div>
          </div>
          {c.notes && (
            <div className="mt-3 text-sm text-[var(--ink-2)] border-t border-[var(--line)] pt-3 whitespace-pre-wrap">{c.notes}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * KanbanPanel — horizontally-scrolling pipeline board grouped by stage.
 *
 * Uses the native HTML5 drag-drop API so we ship zero extra bundle weight.
 * Drop optimistically updates local state, then fires PATCH; failures rollback
 * and toast the error. Each column shows a live count + $ subtotal so the
 * operator can see pipeline value at a glance.
 */
function KanbanPanel() {
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dispo/closings");
      setClosings(data.closings || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const moveTo = async (id, newStage) => {
    const prev = closings;
    const target = prev.find((c) => c.id === id);
    if (!target || target.stage === newStage) return;
    // Optimistic update.
    setClosings((cs) => cs.map((c) => (c.id === id ? { ...c, stage: newStage } : c)));
    try {
      await api.patch(`/dispo/closings/${id}`, { stage: newStage });
      toast.success(`${target.buyer_name || "Deal"} → ${STAGE_LABELS[newStage]}`);
    } catch (e) {
      setClosings(prev);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Move failed");
    }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* no-op */ }
  };
  const onDragEnd = () => { setDragId(null); setDragOver(null); };
  const onDragOver = (stage) => (e) => {
    e.preventDefault();
    if (dragOver !== stage) setDragOver(stage);
  };
  const onDrop = (stage) => (e) => {
    e.preventDefault();
    if (dragId) moveTo(dragId, stage);
    setDragOver(null);
    setDragId(null);
  };

  if (loading) return <div className="p-10 text-center text-[var(--ink-3)]">Loading pipeline…</div>;

  const byStage = Object.fromEntries(STAGE_ORDER.map((s) => [s, []]));
  for (const c of closings) {
    const s = STAGE_ORDER.includes(c.stage) ? c.stage : "opened";
    byStage[s].push(c);
  }
  const totalValue = closings.reduce((sum, c) => sum + (Number(c.offer_price) || 0), 0);
  const openValue = closings
    .filter((c) => !["closed_won", "dead"].includes(c.stage))
    .reduce((sum, c) => sum + (Number(c.offer_price) || 0), 0);

  return (
    <div className="space-y-4" data-testid="kanban-panel">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="rounded-lg border border-[var(--line)] bg-white px-3 py-1.5">
          <span className="text-[var(--ink-3)] text-xs uppercase tracking-widest mr-2">Pipeline</span>
          <span className="font-display font-bold">{closings.length}</span>
          <span className="text-[var(--ink-3)] mx-1.5">·</span>
          <span className="font-display font-bold">{fmtMoney(totalValue)}</span>
        </div>
        <div className="rounded-lg border border-[var(--line)] bg-white px-3 py-1.5">
          <span className="text-[var(--ink-3)] text-xs uppercase tracking-widest mr-2">Open value</span>
          <span className="font-display font-bold text-emerald-600">{fmtMoney(openValue)}</span>
        </div>
        <span className="text-xs text-[var(--ink-3)]">Drag any card to move it between stages.</span>
      </div>

      {closings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--line)] bg-white p-10 text-center" data-testid="kanban-empty">
          <KanbanSquare size={40} className="mx-auto text-[var(--ink-3)] mb-3" />
          <h3 className="font-display font-bold text-xl mb-1">Nothing in your pipeline yet</h3>
          <p className="text-sm text-[var(--ink-2)] max-w-md mx-auto">
            Accept an offer on any Deal Room and hit <b>Send to closing</b> — deals will start populating this board.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-2 -mx-2 px-2" data-testid="kanban-board">
          <div className="flex gap-3 min-w-max">
            {STAGE_ORDER.map((stage) => {
              const items = byStage[stage];
              const value = items.reduce((s, c) => s + (Number(c.offer_price) || 0), 0);
              const color = STAGE_COLOR[stage] || "#94A3B8";
              const isOver = dragOver === stage;
              return (
                <div
                  key={stage}
                  onDragOver={onDragOver(stage)}
                  onDragLeave={() => setDragOver((v) => (v === stage ? null : v))}
                  onDrop={onDrop(stage)}
                  className={`w-64 shrink-0 rounded-xl border bg-white flex flex-col transition ${isOver ? "ring-2 ring-offset-1" : ""}`}
                  style={{
                    borderColor: color + "55",
                    boxShadow: isOver ? `0 0 0 2px ${color}66` : undefined,
                  }}
                  data-testid={`kanban-column-${stage}`}
                >
                  <div
                    className="px-3 py-2 rounded-t-xl flex items-center justify-between"
                    style={{ background: color + "18", borderBottom: `1px solid ${color}44` }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: color }} />
                      <span className="font-semibold text-xs uppercase tracking-widest truncate" style={{ color }}>
                        {STAGE_LABELS[stage]}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-[var(--ink-3)]">{items.length}</span>
                  </div>
                  <div className="px-3 py-1 text-[11px] text-[var(--ink-3)]">
                    {items.length === 0 ? "—" : fmtMoney(value)}
                  </div>
                  <div className="p-2 space-y-2 flex-1 min-h-[120px]">
                    {items.map((c) => (
                      <div
                        key={c.id}
                        draggable
                        onDragStart={onDragStart(c.id)}
                        onDragEnd={onDragEnd}
                        className={`rounded-lg border border-[var(--line)] bg-white p-2.5 cursor-grab active:cursor-grabbing shadow-sm hover:shadow transition ${dragId === c.id ? "opacity-50" : ""}`}
                        data-testid={`kanban-card-${c.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-sm truncate">{c.room_title || "Untitled"}</div>
                            <div className="text-[11px] text-[var(--ink-3)] truncate">{c.property_address}</div>
                          </div>
                          <GripVertical size={12} className="text-[var(--ink-3)] shrink-0 mt-0.5" />
                        </div>
                        <div className="mt-2 flex items-center justify-between text-[11px]">
                          <span className="text-[var(--ink-2)] truncate">{c.buyer_name}</span>
                          <span className="font-display font-bold">{fmtMoney(c.offer_price)}</span>
                        </div>
                        {(c.title_company || c.tc || c.target_close_date) && (
                          <div className="mt-1.5 text-[10px] text-[var(--ink-3)] flex flex-wrap gap-x-2 gap-y-0.5">
                            {c.title_company && <span>🏛️ {c.title_company.name}</span>}
                            {c.tc && <span>👤 {c.tc.name}</span>}
                            {c.target_close_date && <span>🎯 {c.target_close_date}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}



function TeamPanel() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kind: "title", name: "", company: "", email: "", phone: "", notes: "", is_default: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dispo/closing-team");
      setMembers(data.members || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load team");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/dispo/closing-team", form);
      toast.success("Added ✅");
      setShowForm(false);
      setForm({ kind: "title", name: "", company: "", email: "", phone: "", notes: "", is_default: false });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Add failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this team member? Active closings referencing them will be detached.")) return;
    try {
      await api.delete(`/dispo/closing-team/${id}`);
      setMembers((m) => m.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleDefault = async (m) => {
    try {
      const { data } = await api.patch(`/dispo/closing-team/${m.id}`, { is_default: !m.is_default });
      // Reload — defaulting one un-defaults the others of the same kind.
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="team-panel">
      <div className="flex justify-end">
        <button type="button" onClick={() => setShowForm((v) => !v)} className="btn-money" data-testid="team-add-toggle">
          <Plus size={14} />{showForm ? "Close" : "Add team member"}
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="rounded-xl border border-[var(--line)] bg-white p-5 space-y-3" data-testid="team-add-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="field-label">Role</span>
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="input" data-testid="team-form-kind">
                {KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="field-label">Name *</span>
              <input required minLength={1} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="team-form-name" />
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block"><span className="field-label">Company</span><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input" data-testid="team-form-company" /></label>
            <label className="block"><span className="field-label">Email</span><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="team-form-email" /></label>
            <label className="block"><span className="field-label">Phone</span><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" data-testid="team-form-phone" /></label>
          </div>
          <label className="block">
            <span className="field-label">Notes</span>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input min-h-[70px] text-sm" placeholder="Preferred contact, working hours, quirks…" data-testid="team-form-notes" />
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="h-4 w-4 rounded accent-[var(--ink)]" data-testid="team-form-default" />
            Set as my default {KINDS.find((k) => k.v === form.kind)?.l || form.kind}
          </label>
          <button type="submit" className="btn-money" disabled={saving} data-testid="team-form-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}Add
          </button>
        </form>
      )}
      {loading ? (
        <div className="p-10 text-center text-[var(--ink-3)]">Loading…</div>
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--line)] bg-white p-10 text-center" data-testid="team-empty">
          <Users size={40} className="mx-auto text-[var(--ink-3)] mb-3" />
          <h3 className="font-display font-bold text-xl mb-1">Build your closing rolodex</h3>
          <p className="text-sm text-[var(--ink-2)] max-w-md mx-auto">
            Add your go-to Title Company and Transaction Coordinator. Set them as defaults and they&apos;ll auto-populate on every new closing.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="team-list">
          {members.map((m) => (
            <div key={m.id} className="rounded-xl border border-[var(--line)] bg-white p-4" data-testid={`team-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">{KINDS.find((k) => k.v === m.kind)?.l || m.kind}</div>
                  <div className="flex items-center gap-1.5">
                    <div className="font-display font-bold text-base truncate">{m.name}</div>
                    {m.is_default && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#78350F" }}>DEFAULT</span>}
                  </div>
                  {m.company && <div className="text-xs text-[var(--ink-2)] flex items-center gap-1"><Building2 size={11} />{m.company}</div>}
                  {m.email && <a href={`mailto:${m.email}`} className="text-xs text-[var(--ink-2)] flex items-center gap-1 hover:underline"><Mail size={11} />{m.email}</a>}
                  {m.phone && <a href={`tel:${m.phone}`} className="text-xs text-[var(--ink-2)] flex items-center gap-1 hover:underline"><Phone size={11} />{m.phone}</a>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => toggleDefault(m)} title={m.is_default ? "Unset default" : "Set as default"} className={m.is_default ? "text-amber-500" : "text-[var(--ink-3)] hover:text-amber-500"} data-testid={`team-default-${m.id}`}>
                    <Star size={14} fill={m.is_default ? "currentColor" : "none"} />
                  </button>
                  <button type="button" onClick={() => remove(m.id)} className="text-[var(--ink-3)] hover:text-red-600" data-testid={`team-remove-${m.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {m.notes && <div className="mt-2 text-xs text-[var(--ink-2)] whitespace-pre-wrap border-t border-[var(--line)] pt-2">{m.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
